<script>
  import { onMount, getContext } from 'svelte';
  import { refreshAll, goto, beforeNavigate } from '$app/navigation';
  import { fetch_json, format_date, format_date_range, debounce } from '#lib/util.js';
  import { generate_time_slots, MIN_CANCELLATION_MINUTES_HALF_HOURLY_BOOKINGS } from '#lib/patch_util.js';
  import BlockGrid from "#lib/BlockGrid.svelte";
  import Icon from "#lib/Icon.svelte";
  import Alert from "#lib/Alert.svelte";
  import {
    format_price,
    get_iso_week_and_year,
    get_iso_weeks_in_year,
    swipe_navigation,
    setup_kiosk_navigation_prevention,
  } from '#lib/patch_util.js';
  import { 
    format_utc_time_as_local,
    localize_utc_date,
  } from '#lib/util.js';
  import t from '#lib/i18n.svelte.js';
  import ButtonLabel from '#lib/ButtonLabel.svelte';
  import MonthSelector from '#lib/MonthSelector.svelte';
  import RollingNumberDisplay from '#lib/RollingNumberDisplay.svelte';
  import * as env from "$app/env/public";
  import { slide, fade } from 'svelte/transition';
  import Tooltip from '#lib/Tooltip.svelte';
  import { toast } from '#lib/toast.svelte.js';
  import Dialog from '#lib/Dialog.svelte';
  import AccountSwitcher from '#lib/AccountSwitcher.svelte';
  import Avatar from '#lib/Avatar.svelte';
  import BookingSheet from '#lib/BookingSheet.svelte';
  import { BookingSelection, signed_price, storno_count, sheet_labels, billing_line, receipt_lines } from '#lib/booking_selection.svelte.js';
  import { browser, dev } from '$app/env';
  
  // =============================================================================
  // KIOSK MODE TIMER CONSTANTS
  // =============================================================================
  
  /** Time to wait before considering user inactive (milliseconds) */
  const KIOSK_INACTIVITY_THRESHOLD = 20000; // 20 seconds
  
  /** How often to refresh data when user is inactive (milliseconds) */
  const KIOSK_AUTO_REFRESH_INTERVAL = 30000; // 30 seconds
  
  /** How long to wait before resetting to current week (milliseconds) */
  const KIOSK_RESET_TO_TODAY_DELAY = 120000; // 2 minutes
  
  /** Activity tracking throttle delay (milliseconds) */
  const KIOSK_ACTIVITY_THROTTLE = 100; // 100ms
  
  /** Maximum consecutive fetch failures before reload */
  const KIOSK_MAX_FETCH_FAILURES = 3;
  
  /** Time to wait before reloading after persistent connectivity issues (milliseconds) */
  const KIOSK_CONNECTIVITY_RELOAD_DELAY = 120000; // 2 minutes
  
  /** Now indicator and time update interval (milliseconds) */
  const NOW_UPDATE_INTERVAL = 30000; // 30 seconds

  // =============================================================================
  // DEVELOPMENT LOGGING
  // =============================================================================
  
  /** Conditional logging function for development mode only */
  function kiosk_log(...args) {
    if (dev) {
      console.log('Kiosk:', ...args);
    }
  }

  // =============================================================================
  // COMPONENT LOGIC
  // =============================================================================

  let { data } = $props();

  let site_settings = getContext('site_settings');

  let current_user_can_book = $derived(
    data.current_user?.role === 'admin' 
    // also admins when impersonating another user can make bookings on their behalf (bypassing the payment method check)
    || data.authenticated_user?.role === 'admin' 
    || (
      data.current_user?.has_stripe_payment_method === 1 
      // For now we don't require a billing address, but we might in the future
      // && data.current_user?.has_stripe_billing_address === 1
    )
  );

  let dialog = $state();
  
  // We need to make note reactive so it changes when we navigate to a differente route immediately. 
  // Without there's a delay or no udpate at all.
  let note = $state(data.note_for_week);
  $effect(() => {
    note = data.note_for_week;
  });

  function on_close_dialog() {
    dialog = undefined;
  }

  onMount(() => {
    // Check if the instructions dialog has already been shown. A new key since booking needs a
    // confirmation (2026-09): members who knew one-tap booking see the instructions once more.
    const half_hourly_booking_instructions_dialog_shown = localStorage.getItem('half_hourly_booking_confirm_instructions_shown');
    if (!half_hourly_booking_instructions_dialog_shown && data.current_user) {
      dialog = { mode: 'instructions' };
      localStorage.setItem('half_hourly_booking_confirm_instructions_shown', 'true');
    }
  });

  // $inspect('data', data); 
  // $inspect(data.resource.pricing_by_month);
  // $inspect('breakdown_by_month', data.breakdown_by_month);
  // $inspect('month_at_week_start', data.month_at_week_start);
  // $inspect('month_at_week_end', data.month_at_week_end);
  // $inspect('breakdown_by_month_combined', breakdown_by_month_combined);

  let breakdown_by_month_combined = $derived({
    ...data.breakdown_by_month[data.month_at_week_start],
    ...(data.month_at_week_start !== data.month_at_week_end ? data.breakdown_by_month[data.month_at_week_end] : {})
  });

  let month_formatted = $derived(format_date(`2000-${data.week_month_by_weekday_majority}-01`, 'MMMM'));
  let month_and_year_formatted = $derived(format_date(`${data.year}-${data.week_month_by_weekday_majority}-01`, 'YYYYMMMM'));

  let pricing_for_month_at_start_of_week = $derived(data.resource?.pricing_by_month?.[data.month_at_week_start]);
  let pricing_for_month_at_end_of_week = $derived(data.resource?.pricing_by_month?.[data.month_at_week_end]);
  // NOTE: must stay undefined (not NaN) when the month has no pricing — NaN passes the
  // "!== undefined && !== null" guards below and renders an empty price instead of "unavailable"
  let discount_factor_for_month_at_start_of_week = $derived(1 - (data.discounts_and_credits_by_month[data.month_at_week_start]?.meeting_room_discount_percentage ?? 0) / 100);
  let discount_factor_for_month_at_end_of_week = $derived(1 - (data.discounts_and_credits_by_month[data.month_at_week_end]?.meeting_room_discount_percentage ?? 0) / 100);
  let discounted_price_for_month_at_start_of_week = $derived(pricing_for_month_at_start_of_week ? pricing_for_month_at_start_of_week.price * discount_factor_for_month_at_start_of_week : undefined);
  let discounted_price_for_month_at_end_of_week = $derived(pricing_for_month_at_end_of_week ? pricing_for_month_at_end_of_week.price * discount_factor_for_month_at_end_of_week : undefined);
  let discounted_max_price_for_month_at_start_of_week = $derived(pricing_for_month_at_start_of_week ? pricing_for_month_at_start_of_week.max_price * discount_factor_for_month_at_start_of_week : undefined);
  let discounted_max_price_for_month_at_end_of_week = $derived(pricing_for_month_at_end_of_week ? pricing_for_month_at_end_of_week.max_price * discount_factor_for_month_at_end_of_week : undefined);

  // let start_date_of_slots = $derived(Object.keys(data.slots)[0]);
  // let end_date_of_slots = $derived(Object.keys(data.slots)[Object.keys(data.slots).length - 1]);
  // let formatted_date_range = $derived(format_date_range(start_date_of_slots, end_date_of_slots, true, ' – ', true));
  let week_totals = $derived.by(() => {
    // they keys for data.slots are the dates of the slots
    // since the week can span two months, we need to sum the breakdowns for both months
    return Object.keys(data.slots).reduce((totals, date) => {
      const breakdown = breakdown_by_month_combined[date].by_resource[data.resource.entry_id];
      return {
        billable_amount: Math.round(totals.billable_amount + breakdown.billable_amount), // round to nearest integer
        booked_slots: totals.booked_slots + breakdown.booked_slots,
        credits_used: totals.credits_used + breakdown.credits_used
      };
    }, { billable_amount: 0, booked_slots: 0, credits_used: 0 });
  });

  const time_slots = generate_time_slots(30, true);

  // Import PUBLIC_DAY_START from environment variables
  const PUBLIC_DAY_START = parseInt(env.PUBLIC_DAY_START, 10);
  const PUBLIC_DAY_END = parseInt(env.PUBLIC_DAY_END, 10);

  let overflow_wrapper = $state(null);
  let show_month_selector = $state(false);
  let expand_resource_booking_details = $state(false);

  // Scroll to the first slot in the opening time of the day
  $effect(() => {
    if (overflow_wrapper) {
      requestAnimationFrame(() => {
        let slot = null;
        if(Object.keys(data.slots).some(date => date === localize_utc_date(new Date()).toISOString().split('T')[0])) {
          // Scroll to the present slot
          slot = overflow_wrapper.querySelector('.calendar-days-slots .daytime-slot.present');
          if(!slot) {
            // Scroll to the first during opening hours slot
            slot = overflow_wrapper.querySelector('.calendar-days-slots .daytime-slot');
          }
        } else {
          slot = overflow_wrapper.querySelector('.calendar-days-slots .daytime-slot');
        }
        const header_height = overflow_wrapper.querySelector('.calendar-days-legend').offsetHeight;
        const relative_position = slot.getBoundingClientRect().top - slot.getBoundingClientRect().height - overflow_wrapper.getBoundingClientRect().top;
        
        overflow_wrapper.scrollTo({
          top: Math.max(0, relative_position - header_height),
        });
      });
    }
  });

  function reset_ui() {
    expand_resource_booking_details = false;
  }

  function show_add_missing_payment_details_toast() {
    toast.info(
      t('add_missing_payment_details', {href: '/account/billing'}),
      {
        auto_dismiss: true,
        show_close_icon: true,
        id: 'add-missing-payment-details',
      }
    );
  }

  async function save_note() {
    try {
      await fetch_json('POST', '/api/create-or-update-half-hourly-booking-note', {
        resource_id: data.resource.entry_id,
        year: data.year,
        week_number: data.week_number,
        description: note.description
      });
    } catch(err) {
      alert('Notiz konnte nicht gespeichert werden.');
    }
  }
  
  // TODO: refreshAll every X seconds of inactivity (to see bookings being made by other users & changes to availability)

  // =============================================================================
  // SELECTION: nothing is booked or cancelled until the member confirms in the sheet
  // =============================================================================

  // Refusals caused by the slots themselves (not by the account)
  const SLOT_ERRORS = ['SLOT_UNAVAILABLE', 'SLOT_CHANGED', 'INSUFFICIENT_TIME', 'CANCELLATION_TIME_LIMIT', 'OUTSIDE_BUSINESS_HOURS', 'NO_PRICING', 'OUTSIDE_BOOKING_WINDOW'];

  /** Items: `${date}|${slot}` → 'add' | 'remove' */
  const selection = new BookingSelection({
    api: '/api/apply-half-hourly-booking-changes',
    resource_id: () => data.resource.entry_id,
    changes: selection_changes,
    error_message,
    owner: () => `${data.current_user?.user_id}|${data.resource.entry_id}`,
    still_selectable: (key, mode) => {
      const [date, index] = key.split('|');
      return !data.slots[date] || selectable_as(date, Number(index)) === mode;
    },
    // The slots of a refused change, also in weeks that are not shown
    failed_items: ({ error, change }) => {
      if (!SLOT_ERRORS.includes(error)) return null;
      const { booking_date, time_slot_start, time_slot_end } = change;
      return {
        keys: Array.from({ length: time_slot_end - time_slot_start + 1 }, (_, i) => slot_key(booking_date, time_slot_start + i)),
        label: `${format_date(booking_date, 'EEEdMMM')}, ${slot_label(time_slot_start)}–${slot_label(time_slot_end + 1)}`,
      };
    },
    on_refused,
  });
  let pressed_key = $state(null);

  const slot_key = (date, index) => `${date}|${index}`;
  const slot_label = index => time_slots[index] ?? '24:00';

  function has_pricing(date) {
    const pricing = data.resource?.pricing_by_month?.[format_date(date, 'M')];
    return pricing?.price !== undefined && pricing?.price !== null;
  }

  // What a slot can be selected for: free slots to book, own bookings to cancel
  function selectable_as(date, index) {
    const slot = data.slots[date]?.[index];
    if (!slot) return null;
    if (slot.user_state === 'booked-by-me') return slot.cancellable && slot.time_state !== 'past' ? 'remove' : null;
    if (slot.user_state === 'booked-by-none' && !slot.disabled && has_pricing(date)) return 'add';
    return null;
  }

  // The bead kind a slot shows; equal neighbours join into a column
  function kind_of(date, index) {
    const selected = selection.items.get(slot_key(date, index));
    if (selected) return selected;
    return data.slots[date]?.[index]?.user_state === 'booked-by-me' ? 'own' : null;
  }

  function can_start_selecting() {
    if (!data.current_user) {
      goto(`/login?return_to=book/${data.resource.slug}-${data.resource.entry_id}`);
      return false;
    }
    if (!current_user_can_book) {
      show_add_missing_payment_details_toast();
      return false;
    }
    return true;
  }

  // Gestures: tap, mouse drag, Shift-click, touch press-and-hold then drag. Where a drag starts decides
  // what it does: on a selected slot it deselects, on a free slot it selects, on an own booking it
  // selects for cancellation. A range never leaves its day and stops at the first slot of another kind.
  let slots_el = $state();
  let drag = null;
  let last_anchor = null;
  // The click a browser fires after a handled mouse or hold gesture must not toggle those slots again;
  // a tap on another slot right afterwards still counts
  let click_guard = { until: 0, keys: [] };
  const guard_click = (date, ...indexes) => {
    click_guard = { until: performance.now() + 400, keys: indexes.map(index => slot_key(date, index)) };
  };

  const mode_at = (date, index) => selection.items.has(slot_key(date, index)) ? 'unselect' : selectable_as(date, index);

  function paint({ date, anchor, mode, base }, to) {
    const next = new Map(base);
    if (mode === 'unselect') {
      for (let index = Math.min(anchor, to); index <= Math.max(anchor, to); index++) next.delete(slot_key(date, index));
      return next;
    }
    const step = to >= anchor ? 1 : -1;
    for (let index = anchor; ; index += step) {
      if (selectable_as(date, index) !== mode) break;
      next.set(slot_key(date, index), mode);
      if (index === to) break;
    }
    return next;
  }

  function cell_of(element) {
    const cell = element?.closest?.('.slot[data-date]');
    return cell && slots_el?.contains(cell) ? cell : null;
  }

  function on_slots_pointerdown(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0 || is_kiosk_mode) return;
    const cell = cell_of(event.target);
    if (!cell) return;
    const date = cell.dataset.date;
    const index = Number(cell.dataset.slot);
    // Shift extends from the last slot and makes the range match it: selected or not
    const extend = event.shiftKey && last_anchor?.date === date;
    const anchor = extend ? last_anchor.index : index;
    const mode = extend ? (selection.items.get(slot_key(date, anchor)) ?? 'unselect') : mode_at(date, index);
    if (!mode) return; // taken or locked slots explain themselves on click
    if (mode !== 'unselect' && !can_start_selecting()) {
      guard_click(date, index);
      return;
    }
    // A mouse changes the slot on press, so the press itself is the feedback
    drag = { date, anchor, mode, base: selection.items, current: index };
    selection.items = paint(drag, index);
    slots_el.setPointerCapture(event.pointerId);
  }

  function on_slots_pointermove(event) {
    if (!drag || event.pointerType !== 'mouse') return;
    const cell = cell_of(document.elementFromPoint(event.clientX, event.clientY));
    if (!cell || cell.dataset.date !== drag.date) return;
    const index = Number(cell.dataset.slot);
    if (index === drag.current) return;
    drag.current = index;
    selection.items = paint(drag, index);
  }

  function on_slots_pointerup(event) {
    if (!drag || event.pointerType !== 'mouse') return;
    last_anchor = { date: drag.date, index: drag.current };
    guard_click(drag.date, drag.anchor, drag.current);
    drag = null;
    selection.changed();
  }

  function on_slots_click(event) {
    reset_ui();
    if (is_kiosk_mode) {
      track_activity();
      return;
    }
    const cell = cell_of(event.target);
    if (!cell) return;
    const date = cell.dataset.date;
    const index = Number(cell.dataset.slot);
    const slot = data.slots[date][index];
    const key = slot_key(date, index);
    if (performance.now() < click_guard.until && click_guard.keys.includes(key)) return;

    if (selection.items.has(key)) {
      selection.items = new Map([...selection.items].filter(([k]) => k !== key));
      selection.changed();
      return;
    }
    const mode = selectable_as(date, index);
    if (!mode) {
      if (data.current_user?.role === 'admin' && slot.user_state === 'booked-by-other') {
        toast.info(
          t('booked_by_user_details', { name: slot.booked_by_user.name, email: slot.booked_by_user.email }),
          { id: 'booked-by-user-with-name', show_close_icon: true }
        );
      } else if (slot.user_state === 'booked-by-me') {
        toast.error(slot.time_state === 'past'
          ? t('alert_booked_by_me_in_past')
          : t('alert_cancellation_time_limit', { minutes: MIN_CANCELLATION_MINUTES_HALF_HOURLY_BOOKINGS }));
      }
      return;
    }
    if (!can_start_selecting()) return;
    selection.items = new Map(selection.items).set(key, mode);
    last_anchor = { date, index };
    selection.changed();
  }

  // Touch: a tap arrives as a click; press and hold (the growing dot) starts a drag that does not scroll.
  // Needs its own non-passive listeners to stop the scroll and the week swipe while dragging.
  $effect(() => {
    const element = slots_el;
    if (!element || is_kiosk_mode) return;
    let hold = null;

    function start(event) {
      const cell = cell_of(event.target);
      if (!cell || event.touches.length > 1) return;
      const date = cell.dataset.date;
      const index = Number(cell.dataset.slot);
      if (!mode_at(date, index)) return;
      const touch = event.touches[0];
      pressed_key = slot_key(date, index);
      hold = {
        x: touch.clientX,
        y: touch.clientY,
        date,
        index,
        active: false,
        timer: setTimeout(() => {
          pressed_key = null;
          const mode = mode_at(date, index);
          if (!mode || (mode !== 'unselect' && !can_start_selecting())) {
            hold = null;
            return;
          }
          hold.active = true;
          drag = { date, anchor: index, mode, base: selection.items, current: index };
          selection.items = paint(drag, index);
          navigator.vibrate?.(8);
        }, 320),
      };
    }

    function move(event) {
      if (!hold) return;
      const touch = event.touches[0];
      if (!hold.active) {
        if (Math.hypot(touch.clientX - hold.x, touch.clientY - hold.y) > 8) {
          clearTimeout(hold.timer);
          hold = null;
          pressed_key = null;
        }
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const cell = cell_of(document.elementFromPoint(touch.clientX, touch.clientY));
      if (!cell || cell.dataset.date !== drag.date) return;
      const index = Number(cell.dataset.slot);
      if (index === drag.current) return;
      drag.current = index;
      selection.items = paint(drag, index);
    }

    function end() {
      if (!hold) return;
      clearTimeout(hold.timer);
      if (hold.active) {
        guard_click(hold.date, drag.anchor, drag.current);
        last_anchor = { date: hold.date, index: drag.current };
        drag = null;
        selection.changed();
      } else {
        pressed_key = null; // a tap: the click that follows toggles the slot
      }
      hold = null;
    }

    element.addEventListener('touchstart', start, { passive: true });
    element.addEventListener('touchmove', move, { passive: false });
    element.addEventListener('touchend', end);
    element.addEventListener('touchcancel', end);
    return () => {
      clearTimeout(hold?.timer);
      element.removeEventListener('touchstart', start);
      element.removeEventListener('touchmove', move);
      element.removeEventListener('touchend', end);
      element.removeEventListener('touchcancel', end);
    };
  });

  // Contiguous blocks per day and kind, as the API takes them
  function blocks(indexes) {
    return [...indexes].sort((a, b) => a - b).reduce((out, index) => {
      const last = out.at(-1);
      if (last && index === last.to + 1) last.to = index;
      else out.push({ from: index, to: index });
      return out;
    }, []);
  }

  function selection_by_date(items) {
    const by_date = new Map();
    for (const [key, mode] of items) {
      const [date, index] = key.split('|');
      if (!by_date.has(date)) by_date.set(date, { add: [], remove: [] });
      by_date.get(date)[mode].push(Number(index));
    }
    return [...by_date].sort(([a], [b]) => (a < b ? -1 : 1));
  }

  function selection_changes(items) {
    return selection_by_date(items).flatMap(([date, modes]) => [
      ...blocks(modes.add).map(({ from, to }) => ({ booking_date: date, time_slot_start: from, time_slot_end: to, action: 'book' })),
      ...blocks(modes.remove).map(({ from, to }) => ({ booking_date: date, time_slot_start: from, time_slot_end: to, action: 'cancel' })),
    ]);
  }

  let selected_dates = $derived(new Set([...selection.items.keys()].map(key => key.split('|')[0])));

  let summary = $derived.by(() => {
    if (!selection.items.size) return null;
    const { estimate } = selection;
    const now_local = localize_utc_date(new Date());
    let n_add = 0;
    let n_remove = 0;
    let earliest = null;
    const groups = selection_by_date(selection.items).map(([date, modes]) => {
      const add = blocks(modes.add);
      const remove = blocks(modes.remove);
      n_add += modes.add.length;
      n_remove += modes.remove.length;
      for (const block of add) {
        const start = new Date(new Date(`${date}T00:00:00Z`).getTime() + block.from * 30 * 60000);
        if (!earliest || start < earliest) earliest = start;
      }
      return {
        date,
        label: format_date(date, 'EEEEdMMMM'),
        rows: [
          ...add.map(({ from, to }) => ({ kind: 'add', time: `${slot_label(from)}–${slot_label(to + 1)}`, meta: `${to - from + 1} × 30 min` })),
          ...remove.map(({ from, to }) => ({ kind: 'remove', time: `${slot_label(from)}–${slot_label(to + 1)}`, meta: t('booking_cancellation_free') })),
        ],
      };
    });
    const both = n_add > 0 && n_remove > 0;
    const unit = count => t(count === 1 ? 'booking_unit_slot' : 'booking_unit_slots');
    const cancellation_minutes = MIN_CANCELLATION_MINUTES_HALF_HOURLY_BOOKINGS;
    const last_minute = earliest && earliest - now_local < cancellation_minutes * 60000;
    const deadline = earliest && new Date(earliest.getTime() - cancellation_minutes * 60000);
    const where = groups.length === 1
      ? `${format_date(groups[0].date, 'EEEdMMM')}${groups[0].rows.length === 1 ? `, ${groups[0].rows[0].time}` : ''}`
      : t('booking_days', { count: groups.length });

    const month_of_earliest = earliest ? earliest.getUTCMonth() + 1 : data.month_at_week_start;
    const day_max = month_of_earliest === data.month_at_week_end ? discounted_max_price_for_month_at_end_of_week : discounted_max_price_for_month_at_start_of_week;

    return {
      count: n_add || n_remove,
      unit: n_add ? unit(n_add) : t(n_remove === 1 ? 'booking_unit_slot_cancel' : 'booking_unit_slots_cancel'),
      price: n_add && estimate ? signed_price(Math.round(estimate.billable_amount)) : null,
      sub: last_minute ? t('booking_starts_soon_short') : both ? `${where} · ${storno_count(n_remove)}` : where,
      sub_warn: !!last_minute,
      resource: data.resource.title,
      groups,
      estimate: n_add && estimate ? {
        total: signed_price(Math.round(estimate.billable_amount)),
        note: [
          t('plus_vat'),
          estimate.credits_used > 0 ? t('booking_estimate_credits', { amount: format_price(Math.round(estimate.credits_used)) }) : null,
          day_max !== undefined
            ? `${t('booking_estimate_day_max', { amount: format_price(Math.round(day_max)) })}${estimate.capped_dates.length ? ` ${t('booking_cap_reached')}` : ''}`
            : null,
        ].filter(Boolean).join(' · '),
      } : null,
      deadline: n_add ? (last_minute
        ? { warn: true, text: t('booking_starts_soon_no_cancel', { minutes: cancellation_minutes }) }
        : { text: t('booking_free_cancellation_until', { time: `${format_date(deadline.toISOString().slice(0, 10), 'EEEdMMM')}, ${deadline.toISOString().slice(11, 16)}` }) }
      ) : null,
      billing: billing_line(data.current_user),
      ...sheet_labels(n_add, n_remove, unit),
    };
  });

  function error_message({ error }) {
    switch (error) {
      case 'CANCELLATION_TIME_LIMIT': return t('alert_cancellation_time_limit', { minutes: MIN_CANCELLATION_MINUTES_HALF_HOURLY_BOOKINGS });
      case 'INSUFFICIENT_TIME': return t('alert_insufficient_time_remaining');
      case 'SLOT_UNAVAILABLE': return t('alert_slot_unavailable');
      case 'SLOT_CHANGED': return t('booking_error_changed');
      case 'OUTSIDE_BUSINESS_HOURS': return t('outside_business_hours');
      case 'NO_PRICING':
      case 'OUTSIDE_BOOKING_WINDOW': return t('unavailable_for_booking');
    }
  }

  function on_refused(response) {
    // The session has ended
    if (response.error === 'NOT_AUTHORIZED') {
      goto(`/login?return_to=book/${data.resource.slug}-${data.resource.entry_id}`);
    } else if (response.error === 'NO_PAYMENT_METHOD_SET') {
      show_add_missing_payment_details_toast();
    }
  }

  function commit() {
    if (!summary) return;
    selection.commit({ title: summary.receipt_title, lines: receipt_lines(summary.groups), email: data.current_user.email });
  }

  function navigate_adjacent_months(direction) {
    reset_ui();
    track_activity(); // Track activity for kiosk mode

    let year = data.year;
    let week_number = data.week_number;
    // ISO years have 52 or 53 weeks (e.g. 2026 has 53) — roll over accordingly
    if (direction === 'back') {
      if (week_number === 1) {
        year--;
        week_number = get_iso_weeks_in_year(year);
      } else {
        week_number--;
      }
    } else {
      if (week_number >= get_iso_weeks_in_year(year)) {
        year++;
        week_number = 1;
      } else {
        week_number++;
      }
    }
    
    // Preserve kiosk parameter if in kiosk mode
    const kiosk_param = is_kiosk_mode ? '&kiosk=true' : '';
    const new_url = `?year=${year}&week=${week_number}${kiosk_param}`;
    
    // Use navigation controller in kiosk mode for programmatic navigation
    if (is_kiosk_mode && kiosk_navigation_controller) {
      kiosk_navigation_controller.allow_navigation(() => goto(new_url, { replace: true, reset: false }));
    } else {
      goto(new_url, { replace: true, reset: false });
    }
  }

  function handle_month_select({ week, week_year, year, month }) {
    reset_ui();
    track_activity(); // Track activity for kiosk mode

    // Use the business-local date, not raw UTC (they differ around midnight)
    const now_local = localize_utc_date(new Date());
    const current_year = now_local.getUTCFullYear();
    const current_month = now_local.getUTCMonth();

    // For the current month jump to the week containing today; otherwise use
    // the ISO week of the month start. Both are (week, ISO week-based year)
    // pairs — around New Year the ISO year differs from the calendar year.
    const target = (year === current_year && month === current_month)
      ? get_iso_week_and_year(now_local)
      : { week, year: week_year ?? year };

    show_month_selector = false;

    // Preserve kiosk parameter if in kiosk mode
    const kiosk_param = is_kiosk_mode ? '&kiosk=true' : '';
    const new_url = `?year=${target.year}&week=${target.week}${kiosk_param}`;
    
    // Use navigation controller in kiosk mode for programmatic navigation
    if (is_kiosk_mode && kiosk_navigation_controller) {
      kiosk_navigation_controller.allow_navigation(() => goto(new_url, { replace: true, reset: false }));
    } else {
      goto(new_url, { replace: true, reset: false });
    }
  }


  const NIGHT_TIME_SLOT_SCALE = 3;

  function calculate_now_indicator_position() {
    // Create UTC date and localize it to business timezone
    const now_local = localize_utc_date(new Date());
    const hours = now_local.getUTCHours();
    const minutes = now_local.getUTCMinutes();
    
    // Convert current time to slot number (48 slots per day)
    const current_slot = hours * 2 + Math.floor(minutes / 30);
    const slot_progress = (minutes % 30) / 30;
    
    // Calculate total height
    const pre_business_slots = PUBLIC_DAY_START;
    const business_slots = PUBLIC_DAY_END - PUBLIC_DAY_START;
    const post_business_slots = 48 - PUBLIC_DAY_END;
    
    const scale = NIGHT_TIME_SLOT_SCALE; // insides business hours are 3x the height of outside business hours
    const total_height = pre_business_slots +
                        (business_slots * scale) +
                        post_business_slots;

    // Calculate position based on current slot
    let position = 0;    
    if (current_slot < PUBLIC_DAY_START) {
      // Before business hours
      position = (current_slot + slot_progress);
    } else if (current_slot < PUBLIC_DAY_END) {
      // During business hours
      position = PUBLIC_DAY_START + ((current_slot - PUBLIC_DAY_START + slot_progress) * scale);
    } else {
      // After business hours
      position = PUBLIC_DAY_START + (business_slots * scale) + (current_slot - PUBLIC_DAY_END + slot_progress);
    }
    
    return `${ (position / total_height) * 100}%`;
  }

  let now_position = $state(calculate_now_indicator_position());
  let now_time = $state(
    format_utc_time_as_local(new Date())
  );
  
  onMount(() => {
    const interval = setInterval(() => {
      now_position = calculate_now_indicator_position();
      now_time = format_utc_time_as_local(new Date());
    }, NOW_UPDATE_INTERVAL);

    // Setup kiosk mode if enabled
    if (data.is_kiosk_mode) {
      setup_kiosk_timers();
      
      // Add activity listeners
      const activity_events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
      const track_activity_throttled = debounce(track_activity, KIOSK_ACTIVITY_THROTTLE);
      
      activity_events.forEach(event => {
        document.addEventListener(event, track_activity_throttled, { passive: true });
      });
      
      // Setup comprehensive navigation prevention
      kiosk_navigation_controller = setup_kiosk_navigation_prevention({
        on_activity: track_activity
      });
      
      // Setup Service Worker for offline support
      const cleanup_offline_support = setup_kiosk_offline_support();
      
      return () => {
        clearInterval(interval);
        if (refresh_interval) clearInterval(refresh_interval);
        if (reset_to_today_timeout) clearTimeout(reset_to_today_timeout);
        
        activity_events.forEach(event => {
          document.removeEventListener(event, track_activity_throttled);
        });
        
        kiosk_navigation_controller?.cleanup();
        cleanup_offline_support?.();
      };
    }
    
    return () => clearInterval(interval);
  });

  // Kiosk mode state
  let is_kiosk_mode = $derived(data.is_kiosk_mode);
  let last_activity_time = $state(Date.now());
  let refresh_interval = $state(null);
  let reset_to_today_timeout = $state(null);
  let kiosk_navigation_controller = $state(null);

  // Kiosk trap for back/forward and links away from the page (see setup_kiosk_navigation_prevention)
  beforeNavigate((navigation) => kiosk_navigation_controller?.before_navigate(navigation));

  // Activity tracking for kiosk mode
  function track_activity() {
    if (!is_kiosk_mode) return;
    
    last_activity_time = Date.now();
    
    // Clear existing timeouts
    if (refresh_interval) {
      clearInterval(refresh_interval);
      refresh_interval = null;
    }
    if (reset_to_today_timeout) {
      clearTimeout(reset_to_today_timeout);
      reset_to_today_timeout = null;
    }
    
    // Set new timeouts
    setup_kiosk_timers();
  }

  function setup_kiosk_timers() {
    if (!is_kiosk_mode) return;
    
    // Auto-refresh every minute when inactive for 20+ seconds
    refresh_interval = setInterval(() => {
      const time_since_activity = Date.now() - last_activity_time;
      if (time_since_activity >= KIOSK_INACTIVITY_THRESHOLD) {
        refreshAll();
      }
    }, KIOSK_AUTO_REFRESH_INTERVAL);
    
    // Reset to today after 2 minutes of inactivity
    reset_to_today_timeout = setTimeout(async () => {
      const now_local = localize_utc_date(new Date());
      // ISO week/year pair — around New Year the ISO year differs from the calendar year
      const { week: current_week, year: current_week_year } = get_iso_week_and_year(now_local);

      if (data.year !== current_week_year || data.week_number !== current_week) {
        // Use the navigation controller to allow this programmatic navigation
        if (kiosk_navigation_controller) {
          await kiosk_navigation_controller.allow_navigation(() =>
            goto(`?year=${current_week_year}&week=${current_week}&kiosk=true`, { replace: true, reset: false })
          );
        }
      }
    }, KIOSK_RESET_TO_TODAY_DELAY);
  }

  // Service Worker setup for offline support in kiosk mode
  function setup_kiosk_offline_support() {
    if (!is_kiosk_mode || !browser || !('serviceWorker' in navigator)) return;
    
    // Double-check that we're actually in kiosk mode by checking URL
    const url_params = new URLSearchParams(window.location.search);
    if (!url_params.has('kiosk')) {
      kiosk_log('SW: Not registering - kiosk parameter not found in URL');
      return;
    }
    
    let sw_registration = null;
    
    // Register the kiosk service worker
    navigator.serviceWorker.register('/kiosk-sw.js', {
      scope: '/'
    }).then(registration => {
      kiosk_log('SW: Registered successfully for kiosk mode');
      sw_registration = registration;
      
      // Cache current page immediately
      if (registration.active) {
        registration.active.postMessage({
          type: 'CACHE_CURRENT_PAGE',
          url: window.location.href
        });
      }
      
      // Listen for updates
      registration.addEventListener('updatefound', () => {
        const new_worker = registration.installing;
        new_worker.addEventListener('statechange', () => {
          if (new_worker.state === 'installed' && navigator.serviceWorker.controller) {
            kiosk_log('SW: New version available, auto-updating...');
            // Auto-update in kiosk mode
            new_worker.postMessage({ type: 'SKIP_WAITING' });
          }
        });
      });
    }).catch(error => {
      kiosk_log('SW: Registration failed:', error);
    });
    
    // Listen for controller changes (SW updates)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      kiosk_log('SW: Controller changed, reloading...');
      window.location.reload();
    });
    
    // Simple connectivity monitoring (much simplified)
    let consecutive_failures = 0;
    const max_failures = KIOSK_MAX_FETCH_FAILURES;
    let failure_timeout = null;
    
    // Override fetch to count failures
    const original_fetch = window.fetch;
    window.fetch = async (...args) => {
      try {
        const response = await original_fetch(...args);
        
        if (response.ok) {
          consecutive_failures = 0;
          if (failure_timeout) {
            clearTimeout(failure_timeout);
            failure_timeout = null;
          }
        }
        
        return response;
      } catch (error) {
        consecutive_failures++;
        
        if (consecutive_failures >= max_failures && !failure_timeout) {
          kiosk_log('Multiple failures detected, will reload in 2 minutes if not resolved');
          failure_timeout = setTimeout(() => {
            kiosk_log('Reloading due to persistent issues');
            window.location.reload();
          }, KIOSK_CONNECTIVITY_RELOAD_DELAY);
        }
        
        throw error;
      }
    };
    
    // Cleanup function
    return () => {
      if (failure_timeout) clearTimeout(failure_timeout);
      window.fetch = original_fetch;
      
      // Only unregister if we're leaving kiosk mode
      if (sw_registration) {
        const current_url_params = new URLSearchParams(window.location.search);
        if (!current_url_params.has('kiosk')) {
          kiosk_log('SW: Unregistering service worker - leaving kiosk mode');
          sw_registration.unregister().catch(console.error);
        }
      }
    };
  }

  // Find current and upcoming bookings for greeting
  let current_and_upcoming_booking = $derived.by(() => {
    if (!is_kiosk_mode) return { current: null, upcoming: null };
    
    const now_local = localize_utc_date(new Date());
    const current_date = now_local.toISOString().split('T')[0];
    
    const today_slots = data.slots[current_date];


    if (!today_slots) return { current: null, upcoming: null };
    
    // Find current booking
    // First try to find a slot marked as "present"
    let current_slot = today_slots.find(slot => 
      slot.time_state === 'present' && 
      ['booked-by-other', 'booked-by-me'].includes(slot.user_state) &&
      slot.booked_by_user
    );
    
    // If no present slot found, look for the last past slot that's booked
    // (this would be the ongoing meeting that's now past the 15min mark)
    if (!current_slot) {
      const first_future_index = today_slots.findIndex(slot => slot.time_state === 'future');
      if (first_future_index > 0) {
        const potential_current_slot = today_slots[first_future_index - 1];
        if (potential_current_slot.time_state === 'past' && 
            ['booked-by-other', 'booked-by-me'].includes(potential_current_slot.user_state) &&
            potential_current_slot.booked_by_user) {
          current_slot = potential_current_slot;
        }
      }
    }

    const current_booking = current_slot?.booked_by_user || null;

    
    // Find upcoming booking (first future slot that's booked)
    const upcoming_slot = today_slots.find(slot => 
      slot.time_state === 'future' && 
      ['booked-by-other', 'booked-by-me'].includes(slot.user_state) &&
      slot.booked_by_user
    );

    const upcoming_booking = upcoming_slot?.booked_by_user || null;

    
    return { current: current_booking, upcoming: upcoming_booking };
  });
</script>

{#snippet footer_details(is_mobile)}
  <div class="footer-details-content flex-column gap-y g-4 pbe p-6">
    <!-- Booking on behalf of + overview of booking for this week -->
    <div class="flex-wrap gap-x gap-y g-4 justify-between full-width items-start px p-6">

      <div class='flex-column gap-y g-3 fit-content text-right'>
        <!-- Account Switcher -->
        {#if data.current_user}
          <div class='flex-column gap-y g-1'>
            <p class='text-preamble text-left mbe m-2'>
              {data.current_user.role === 'admin' ? 'Intern (keine Abrechnung)' : t('booking_on_invoice_of')}
            </p>
            {#key data.current_user.user_id}
              <AccountSwitcher container='dropdown' />
            {/key}
          </div>
        {:else}
          <a href="/login?return_to=book/{data.resource.slug}-{data.resource.entry_id}">
            <ButtonLabel variant='brand {site_settings.primary_control_surface}'>
              {t('login_and_book')}
            </ButtonLabel>
          </a>
        {/if}
        
  
        <!-- Bookings Overview: Costs for this week -->
        {#if week_totals?.booked_slots > 0}
          <div transition:fade={{duration:100}} class='bookings-overview flex-row gap-x g-3 items-center fit-content'>
            <p class='text-caption leading-trim' style='min-height: 1em' transition:fade={{duration:100}}>{t('this_week')}:</p>
            {#if (week_totals.billable_amount > 0 )}
              <RollingNumberDisplay 
                number={format_price(week_totals.billable_amount)} 
                class='text-caption -bold'
              />
            {:else}
              <p class='text-preamble leading-trim' style='min-height: 1em'>{t('free_using_credits')}</p>
            {/if}
          </div>
        {/if}
      </div>

      <!-- Pricing Info -->
      <div class='resource-overview flex-column gap-y g-3 fit-content'>
        <!-- Only show pricing if at least one of the pricings exists -->
        {#if !(discounted_price_for_month_at_start_of_week !== undefined && discounted_price_for_month_at_start_of_week !== null) && !(discounted_price_for_month_at_end_of_week !== undefined && discounted_price_for_month_at_end_of_week !== null)}  
          {#if data.current_user?.role === 'admin'}
            <Alert icon='warning'>
              <p class='text-caption'>{t('pricing_not_configured')}. {t('unavailable_for_booking')}</p>
            </Alert>
          {:else}
            <p class='text-preamble text-3 leading-trim'>{t('unavailable_for_booking')}</p>
          {/if}
        {:else}
          <!-- Show pricing for start of week if it exists -->
          {#if discounted_price_for_month_at_start_of_week !== undefined && discounted_price_for_month_at_start_of_week !== null}
            <div class='flex-column gap-y g-3'>
              <p class='{is_mobile? 'text-caption' :'text-body'} leading-trim'>{format_price( parseInt(Math.round(discounted_price_for_month_at_start_of_week), 10))}&thinsp;/&thinsp;30min</p>
              <p class='{is_mobile? 'text-caption' :'text-body'} leading-trim nowrap'>{t('max')} {format_price( parseInt(Math.round(discounted_max_price_for_month_at_start_of_week), 10))}&thinsp;/&thinsp;{t('day')}</p>
              <p class='text-caption leading-trim text-3'>{t('plus_vat')}</p>
            </div>
          {/if}

          <!-- Show end of week pricing if it differs or if only end pricing exists -->
          {#if (
            (discounted_price_for_month_at_end_of_week !== undefined && discounted_price_for_month_at_end_of_week !== null) &&
            (!(discounted_price_for_month_at_start_of_week !== undefined && discounted_price_for_month_at_start_of_week !== null) ||
            discounted_price_for_month_at_end_of_week !== discounted_price_for_month_at_start_of_week ||
            discounted_max_price_for_month_at_end_of_week !== discounted_max_price_for_month_at_start_of_week)
          )}
            <div class='border-1-system px py p-4'>
              <div class='flex-column gap-y g-3'>
                <p class='{is_mobile? 'text-caption' :'text-body'} leading-trim'>{t('new_prices_from')} {format_date(`${data.year}-${data.month_at_week_end}-01`, 'dMMM')}</p>
                <p class='{is_mobile? 'text-caption' :'text-body'} leading-trim'>{format_price( parseInt(Math.round(discounted_price_for_month_at_end_of_week), 10))}&thinsp;/&thinsp;30min</p>
                <p class='{is_mobile? 'text-caption' :'text-body'} leading-trim nowrap'>{t('max')} {format_price( parseInt(Math.round(discounted_max_price_for_month_at_end_of_week), 10))}&thinsp;/&thinsp;{t('day')}</p>
                <p class='text-caption leading-trim text-3'>{t('plus_vat')}</p>
              </div>
            </div>
          {/if}
        {/if}
      </div>
    </div>

    <!-- Same as in DailyBooking.svelte -->
    {#if data.current_user && !current_user_can_book}
      <div class='flex-column gap-y g-4 px p-6'>
        {#if data.account_switcher_users.length >= 1}
          <Alert variant='brand' icon='warning'>
            <p class='text-caption'>{@html t('add_missing_payment_details_or_switch_account', {href: '/account/billing'})} </p>
          </Alert>
        {:else}
          {#if data.current_user.has_stripe_payment_method === 0}
            <Alert variant='brand' icon='warning'>
              <p class='text-caption'>{@html t('add_missing_payment_details', {href: '/account/billing'})} </p>
            </Alert>
          {:else if data.current_user.has_stripe_billing_address === 0}
            <Alert variant='brand' icon='warning'>
              <p class='text-caption'>{@html t('add_missing_billing_address', {href: '/account/billing'})} </p>
            </Alert>
          {/if}
          {#if !data.account_switcher_users.length}
            <Alert variant='brand'>
              <p class='text-caption'>{@html t('book_on_behalf_of_company_team_member_hint', {email: data.current_user.email})}</p>
            </Alert>
          {/if}
        {/if}
      </div>
    {/if}
  </div>
{/snippet}

{#snippet footer(screen_size)}
  {@const is_mobile = screen_size === 'mobile'}
  
  {#if is_kiosk_mode}
    <!-- Kiosk Mode Footer -->
    <footer class='{screen_size} kiosk-mode flex-column items-center justify-center'>
      <div class='footer-card flex-column {is_mobile ? 'border-lg border-1 shadow-md overflow-hidden ' : ''}'>
        <div class='footer-header flex-column gap-y {is_mobile ? 'g-4' : 'g-8'} px py p-6'>
          
          <!-- Resource Title -->
          <div class='flex-column'>
            <p class='text-{is_mobile ? 'heading3' : 'heading1'} leading-trim text-center'>{data.resource.title}</p>
          </div>
          
            
          <!-- Greeting Message -->
          <div class='flex-column bg-emphasis text-emphasis px py p-2'>
            {#if current_and_upcoming_booking.current}
              <p class='text-body text-center'>{t('welcome_name', {name: current_and_upcoming_booking.current.initials})}</p>
              <p class='text-body text-center'>{t('your_meeting_is_in_progress')}</p>
            {:else if current_and_upcoming_booking.upcoming}
              <p class='text-body text-center'>{t('welcome_name', {name: current_and_upcoming_booking.upcoming.initials})}</p>
              <p class='text-body text-center'>{t('your_meeting_starts_soon')}</p>
            {:else}
              <p class='text-body text-center'>{t('meeting_room_available')}</p>
            {/if}
            
          </div>

          <!-- QR Code Instructions -->
          <div class='flex-column gap-y g-2'>
            <p class='text-{is_mobile ? 'body' : 'heading3'} text-center'>{t('book_via_app')}:</p>
            <p class='text-body text-center'>{data.origin.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}</p>
          </div>

        </div>
      </div>
    </footer>
  {:else}
    <!-- Normal Mode Footer (on mobile the booking sheet takes its place while something is selected) -->
    <footer class='{screen_size} flex-column' class:covered={is_mobile && (summary || selection.receipt)}>
      <div class='footer-card flex-column {is_mobile ? 'border-lg border-1 shadow-md overflow-hidden ' : ''}'>


        <!-- TODO: change this to wrap the button only around the header -->
        <!-- TODO: add tooltip to the alert if bookings are without payment method -->
        
        <div class='footer-header flex-column'>

          <!-- Clickable header to expand/collapse the booking details -->
          <svelte:element 
            this={is_mobile ? 'button' : 'div'} class='flex-column py px p-6' 
            onclick={(e) => {
              e.preventDefault();
              if(is_mobile) {
                expand_resource_booking_details = !expand_resource_booking_details
              }
            }}
            role={is_mobile ? 'button' : ''}
          >
            <div class='flex-{is_mobile ? 'row justify-between items-center' : 'column'} gap-x g-3 full-width'>
              <div class='flex-row items-center gap-x g-3'>
                {#if is_mobile && data.current_user}
                  <Icon name={expand_resource_booking_details ? 'arrow-up' : 'arrow-down'} />
                {/if}
                <p class='{is_mobile ? 'text-body' : 'text-heading3'} leading-trim' style='min-height: 1em; text-wrap: balance'>{data.resource.title}</p>
              </div>
              {#if is_mobile}
                <div class='flex-row gap-x g-3 items-center'>
                  {#if week_totals?.booked_slots > 0}
                    <div class='bookings-overview flex-row gap-x g-3 items-center fit-content'>
                      {#if (week_totals.billable_amount > 0 )}
                        <RollingNumberDisplay 
                          number={format_price(week_totals.billable_amount)} 
                          class='text-caption -bold'
                        />
                      {:else}
                        <p class='text-preamble leading-trim' style='min-height: 1em'>{t('free_using_credits')}</p>
                      {/if}
                    </div>
                  {/if}
                  {#if discounted_price_for_month_at_start_of_week !== undefined && discounted_price_for_month_at_start_of_week !== null}
                    <p class='text-caption text-3 leading-trim nowrap'>{format_price( parseInt(Math.round(discounted_price_for_month_at_start_of_week), 10))}&thinsp;/&thinsp;30min</p>
                  {/if}
                </div>
              {/if}
            </div>
          </svelte:element>

          {#if !is_mobile}
            <!-- Booking on behalf of + overview of booking for this week (always visible on desktop) -->
            {@render footer_details(is_mobile)}
          {/if}

        </div>
        
        <!-- Details: Bookings Breakdown with costs for this week, monthly credits, etc. -->
        {#if data.current_user && (expand_resource_booking_details || !is_mobile)}
          <div class='booking-details flex-column' transition:slide={{ duration: 250}}>

            {#if is_mobile}
              {@render footer_details(is_mobile)}
            {/if}

            <div class='breakdown-table flex-{is_mobile ? 'row' : 'column'} justify-between {is_mobile ? 'items-end' : 'gap-y g-10'} px pbe p-6'>
              <table class='full-width'>
                <tbody>
                  <!-- <tr>
                    <td class='py p-1 text-caption'>{t('week')} {data.week_number}</td> 
                    <td class='ps py p-1 text-right text-caption'>{formatted_date_range}</td> 
                  </tr> -->
                  <!-- {#if week_totals.booked_slots > 0}
                    <tr>
                      <td class='py p-1 text-caption'>{week_totals.booked_slots === 1 ? t('booking_this_week') : t('bookings_this_week')}</td> 
                      <td class='ps py p-1 text-right text-caption'>{week_totals.booked_slots}×</td> 
                    </tr>
                  {/if} -->
                  {#if data.breakdown_by_month[data.week_month_by_weekday_majority].totals.billable_amount > 0}
                    <tr>
                      <td class='py p-1 text-caption'>{t('bookings_for_month', {month: month_formatted})}</td> 
                      <td class='ps py p-1 text-right text-caption'>{data.breakdown_by_month[data.week_month_by_weekday_majority].totals.booked_slots}×</td> 
                    </tr>
                  {/if}
                  {#if data.breakdown_by_month[data.week_month_by_weekday_majority].totals.credits_used > 0}
                    <tr style="border-top: 1px solid oklch(0 0 0 / 0.1)">
                      <td class='py p-1 text-caption'>{t('credits_in_month', {month: month_formatted})}</td> 
                      <td class='ps py p-1 text-right text-caption'>{format_price( 
                        parseInt(Math.round(data.discounts_and_credits_by_month[data.week_month_by_weekday_majority]?.meeting_room_credit_allowance), 10) 
                      )}</td> 
                    </tr>
                    <tr>
                      <td class='py p-1 text-caption'>{t('credits_used')}</td> 
                      <td class='ps py p-1 text-right text-caption'>-{format_price(
                        parseInt(Math.round(data.breakdown_by_month[data.week_month_by_weekday_majority].totals.credits_used), 10) 
                      )}</td> 
                    </tr>
                  {/if}
                  <tr>
                    <td class='py p-1 text-caption'>{t('available_credits')}</td> 
                    <td class='ps py p-1 text-right text-caption'>{format_price( 
                      parseInt(
                        Math.round(
                          (data.discounts_and_credits_by_month[data.week_month_by_weekday_majority]?.meeting_room_credit_allowance ?? 0) - 
                          (data.breakdown_by_month[data.week_month_by_weekday_majority].totals.credits_used ?? 0)
                        ), 10
                      ) ?? 0 
                    )}</td> 
                  </tr>
                  <tr style="border-top: 1px solid oklch(0 0 0 / 0.1)">
                    <td class='py p-1 text-caption -bold'>{t('total_for_month', {month: month_formatted})}</td> 
                    <td class='ps py p-1 text-right text-caption -bold'>{format_price( 
                      parseInt(Math.round(data.breakdown_by_month[data.week_month_by_weekday_majority].totals.billable_amount), 10) 
                    )}</td> 
                  </tr>
                <!-- <p class='text-caption leading-trim'><span class='text-3'>{t('credits_used_for_this_week')}:</span> {format_price(Math.round(week_totals.credits_used))} </p> -->
                </tbody>
              </table>
            </div>

            <!-- NOTE: We show notes for admins or when an admin is impersonating someone -->
            {#if data.current_user?.role === 'admin' || data.authenticated_user?.role === 'admin'}
              <div class='flex-column px p-6 mbe m-4'>
                <label class='flex-row gap-x g-2 items-center py p-2' for='note_for_week'>
                  <Icon name='pencil' />
                  <p class='text-preamble'>{t('notes')} {t('week')} {data.week_number}</p>
                </label>
                <textarea
                  id='note_for_week'
                  class='outlined text-caption'
                  bind:value={note.description}
                  oninput={debounce(save_note, 1000)} 
                  placeholder={t('add_internal_booking_notes')}
                />
                {#if note.last_updated_by}
                  <p class='text-caption text-3 text-right self-end'>({t('last_update_by',{name: note.last_updated_by})})</p>
                {/if}
              </div>
            {/if}

          </div>
        {/if}
      </div>



      <div class='instructions-desktop-tooltip justify-end px py p-6 full-width'>
        {#if !is_kiosk_mode}
          <button onclick={() => {track_activity(); dialog = {mode: 'instructions'}}}>
            <ButtonLabel variant='brand ghostly' start_icon='tooltip-circle-outline' />
          </button>
        {/if}
       </div>

    </footer>
  {/if}
{/snippet}

<BlockGrid>

  <div class="booking-column">
  <div
    class="overflow-wrapper half-hourly-booking"
    class:kiosk-mode={is_kiosk_mode}
    bind:this={overflow_wrapper}
    use:swipe_navigation={{ on_navigate: navigate_adjacent_months }}
  >
    {#if show_month_selector}
      <MonthSelector 
        week_number={data.week_number}
        on_select={handle_month_select}
      />        
    {:else}
      <div class="calendar-content flex-column full-width relative" class:kiosk-mode={is_kiosk_mode} style:--night-time-slot-scale={NIGHT_TIME_SLOT_SCALE}>
        <div class="calendar-days-legend grid-8-columns shadow-md">
          <div class='row-1 flex-row justify-between'>
            
            <!-- Previous day button -->
            <button class='date-nav-btn previous-day' onclick={() => navigate_adjacent_months('back')} title={t('previous_week')}>
              <ButtonLabel variant='brand ghostly' start_icon="arrow-left" />
            </button>

            <!-- Month selector button -->
            <button onclick={()=>{track_activity(); show_month_selector = true}}>
              <ButtonLabel variant='brand ghostly' start_icon="calendar" end_icon="small-arrow-down">
                {month_and_year_formatted}
              </ButtonLabel>
            </button>

            <!-- Next day button -->
            <button class='date-nav-btn next-day justify-end' onclick={() => navigate_adjacent_months('forward')} title={t('next_week')}>
              <ButtonLabel variant='brand ghostly' end_icon="arrow-right" />
            </button>
            
          </div>
          <div class='legend-item items-center relative py p-2'>
          </div>
          <!-- Days of the week -->
          {#each Object.keys(data.slots) as date, index}
            {@const resource_sum = breakdown_by_month_combined[date].by_resource[data.resource.entry_id]}
            {@const price_basis = resource_sum.price_basis}
            {@const amount = format_price( parseInt(Math.round(resource_sum.amount), 10), {decimal_digits: 0})}
            {@const credits_used = format_price( parseInt(Math.round(resource_sum.credits_used), 10))}
            {@const billable_amount_integer = format_price( parseInt(Math.round(resource_sum.billable_amount), 10), {decimal_digits: 0})}
            {@const billable_amount = format_price( parseInt(Math.round(resource_sum.billable_amount), 10), {decimal_digits: 0})}
            {@const discount_percentage = resource_sum.discount_percentage}
            <div class='legend-item items-center relative py p-2'>
              <div class='weekday text-preamble text-center'>
                {format_date(date,'E')}
              </div>
              <div 
                class='day-number text-body text-center'
                class:today={date === localize_utc_date(new Date()).toISOString().split('T')[0]}
              >
                <span>
                  {format_date(date,'d')}
                </span>
                {#if format_date(date, 'd') === '1'}
                  <small class='month text-body -size-reduce text-center'>
                    {format_date(date,'MMM')}
                  </small>
                {/if}
                {#if selected_dates.has(date)}
                  <span class='selection-dot' aria-hidden='true'></span>
                {/if}
              </div>
                {#if resource_sum.booked_slots > 0}
                  <div class="dayly-price full-height flex-column items-center">
                    <Tooltip 
                      variant='ghostly'
                      class='full-width full-height flex-column'
                      show_icon={false}
                      placement={index < 2 ? 'bottom-start' : index > 4 ? 'bottom-end' : 'bottom-center'}
                    >
                      {#snippet title()}
                        <div class='text-caption text-3 flex-column items-center'>
                          {#if resource_sum.amount !== resource_sum.billable_amount}
                            <span class='-strike'>{amount}</span> 
                          {/if}
                          <RollingNumberDisplay number={billable_amount_integer} />
                        </div>
                      {/snippet}
                      {#snippet description()}
                        <div class='flex-column gap-y g-1'>
                          <p class='text-body text-3'>{t('price_breakdown')}</p>
                          <table><tbody>

                            <!-- Pricing method -->
                            <tr>
                              <td class='text-caption py p-1 text-3'>{t('pricing_method')}</td>
                              <td class='text-caption ps p-2 text-right text-3'>{t(price_basis)}</td>
                            </tr>
                            
                            {#if discount_percentage > 0}
                              <tr>
                                <td class='text-caption py p-1'>{t('membership_savings')}</td>
                                <td class='text-caption ps p-2 text-right'>{discount_percentage}%</td>
                              </tr>
                            {/if}

                            <!-- Price calculation -->
                            <tr>
                              <td class='text-caption py p-1'>{discount_percentage > 0 ? t('discounted_price') : t('base_price')}</td>
                              <td class='text-caption ps p-2 text-right'>{format_price( parseInt(Math.round(resource_sum.amount), 10))}</td>
                            </tr>
                                                        
                            <!-- Show credit application if credits were used -->
                            {#if resource_sum.credits_used > 0}
                              <tr>
                                <td class='text-caption py p-1'>{t('credits_used')}</td>
                                <td class='text-caption ps p-2 text-right'>-{credits_used}</td>
                              </tr>
                            {/if}
                            
                            <!-- Final billable amount -->
                            <tr style="border-top: 1px solid oklch(1 0 0 / 0.2)">
                              <td class='text-caption py p-1 -bold'>{t('total_to_pay')}</td>
                              <td class='text-caption -bold ps p-2 text-right'>{billable_amount}</td>
                            </tr>
                          </tbody></table>
                        </div>
                      {/snippet}
                    </Tooltip>
                  </div>
                {/if}
            </div>
          {/each}
        </div>

        <!-- Slot buttons handle keyboard presses themselves (they arrive here as clicks) -->
        <!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_static_element_interactions, a11y_no_noninteractive_element_interactions -->
        <ol
          class='calendar-days-slots grid-8-columns full-width'
          bind:this={slots_el}
          onclick={on_slots_click}
          onpointerdown={on_slots_pointerdown}
          onpointermove={on_slots_pointermove}
          onpointerup={on_slots_pointerup}
          onpointercancel={on_slots_pointerup}
        >

          <li class='time-slot-legend'>
            {#each {length: 48} as _, index}
              <div 
                class='time-slot text-caption text-3 {index >= PUBLIC_DAY_START && index < PUBLIC_DAY_END ? 'daytime-slot' : 'nighttime-slot'}'>
                {time_slots[index]}
              </div>
            {/each}
          </li>
            {#each Object.keys(data.slots) as date, i}
              {@const slots = data.slots[date]}
              {@const month = format_date(date, 'M')}
              <!-- Disable booking if no pricing is defined for this month -->
              {@const has_month_pricing = data.resource?.pricing_by_month?.[month]?.price !== undefined && data.resource?.pricing_by_month?.[month]?.price !== null}

              {#if localize_utc_date(new Date()).toISOString().split('T')[0] === date}
                <div class='now-indicator' style='top: {now_position}'>
                  <span class='text-caption leading-trim px py p-2 border-round -caps'>{now_time}</span>
                </div>    
              {/if}

              <li class="date-column weekday-{i} flex-column">
                {#each slots as slot, index}
                  {@const is_admin_viewing_other_users_booking = data.current_user?.role === 'admin' && slot.user_state === 'booked-by-other'}
                  {@const selected = selection.items.get(slot_key(date, index))}
                  {@const kind = kind_of(date, index)}
                  <button
                    disabled={(slot.disabled || !has_month_pricing) && !is_admin_viewing_other_users_booking}
                    data-date={date}
                    data-slot={index}
                    aria-pressed={selected ? 'true' : undefined}
                    class={[
                      'slot',
                      slot.time_state,
                      index >= PUBLIC_DAY_START && index < PUBLIC_DAY_END
                        ? 'daytime-slot'
                        : 'nighttime-slot',
                      slot.business_hours_state,
                      slot.user_state,
                      selected && `pend pend-${selected}`,
                      kind && kind === kind_of(date, index - 1) && 'chain-up',
                      kind && kind === kind_of(date, index + 1) && 'chain-down',
                      pressed_key === slot_key(date, index) && 'pressed',
                    ].filter(Boolean).join(' ')}
                    class:booked-by-other-view-for-admin={is_admin_viewing_other_users_booking}
                  >
                    {#if is_admin_viewing_other_users_booking}
                      <Avatar 
                        avatar={slot.booked_by_user.avatar}
                        name={slot.booked_by_user.name}
                      />
                    {/if}
                  </button>
                {/each}
              </li>
            {/each}
          </ol>
          {@render footer('mobile')}
      </div>
    {/if}
  </div>
  {#if !is_kiosk_mode}
    <BookingSheet
      {summary}
      receipt={selection.receipt}
      error={selection.error}
      busy={selection.busy}
      stale={selection.estimate_stale}
      on_commit={commit}
      on_clear={() => selection.clear()}
      on_done={() => selection.dismiss_receipt()}
    />
  {/if}
  </div>

  {@render footer('desktop')}

  <!-- <pre><code>{JSON.stringify(data.breakdown_by_month_combined, null, '  ')}</code></pre> -->
</BlockGrid>

{#if ['instructions'].includes(dialog?.mode)}
  <Dialog onclose={on_close_dialog} class='drawer py p-6 border-md'>
    {#if dialog?.mode === 'instructions'}
      <div class='instructions flex-column gap-y g-7'>
        <div class='flex-row items-center gap-x g-7 justify-between'>
          <p class='text-heading3'>{t('how_to_reserve_a_meeting_room')}</p>
          <button onclick={on_close_dialog}>
            <ButtonLabel variant='brand outlined' start_icon='close' />
          </button>
        </div>
        <div class='flex-column gap-y g-6'>
          <p class='text-body leading-trim'>{t('click_to_book')}</p>
          <p class='text-body leading-trim'>{t('click_to_cancel')}</p>
          <p class='text-body leading-trim'>{t('cancel_free_until_minutes_before_booking', { minutes: MIN_CANCELLATION_MINUTES_HALF_HOURLY_BOOKINGS })}</p>
        </div>
        <hr>
        <div class='flex-column gap-y g-3'>
          <p class='stext-body'>{t('slot_symbols_explained')}:</p>
          <div class='flex-row items-center gap-x g-3'>
            <div class='slot booked-by-me'></div>
            <p class='text-caption text-2'>
              {#if data.current_user?.user_id === data.authenticated_user?.user_id}
                {@html t('reservations_for_authenticated_user', {name: data.current_user.name})}
              {:else if (data.current_user)}
                {@html t('reservations_for_current_user', {name: data.current_user?.name})}
              {/if}
            </p>
          </div>
          <div class='flex-row items-center gap-x g-3'>
            <div class='slot pend pend-add'></div>
            <p class='text-caption text-2'>{t('booking_legend_selected')}</p>
          </div>
          <div class='flex-row items-center gap-x g-3'>
            <div class='slot booked-by-me pend pend-remove'></div>
            <p class='text-caption text-2'>{t('booking_legend_selected_cancel')}</p>
          </div>
          <div class='flex-row items-center gap-x g-3'>
            <div class='slot booked-by-other'></div>
            <p class='text-caption text-2'>{t('unavailable')}</p>
          </div>
          <div class='flex-row items-center gap-x g-3'>
            <div class='slot outside-business-hours'></div>
            <p class='text-caption text-2'>{t('outside_business_hours')}</p>
          </div>
        </div>
      </div>
    {/if}
  </Dialog>
{/if}

<style lang="postcss">
  /* :global(.main-page-content:has([style*="position: sticky"]) #dynamic-island) {
    position: static;
  } */

  :global(body:has(.half-hourly-booking.kiosk-mode)) {
    --dynamic-island-height: 0px;
    --dynamic-island-bottom-edge: 0px;
    :global(.dynamic-island) {
      display: none;
    }
    /* Prevent swipe navigation */
    overscroll-behavior-x: none;
    -webkit-overflow-scrolling: auto;
    /* Disable text selection in kiosk mode */
    user-select: none;
    -webkit-user-select: none;
    /* Prevent context menu */
    -webkit-touch-callout: none;
  }

  .half-hourly-booking.kiosk-mode {
    /* Additional swipe prevention */
    overscroll-behavior: none;
    /* Disable pull-to-refresh */
    overscroll-behavior-y: none;
    /* Prevent horizontal scrolling */
    overflow-x: hidden;
  }

  footer {
    &.mobile {
      @media (min-width: 481px) {
        padding: var(--size-4);
      }
      @media (min-width: 1025px) {
        display: none;
      }
      position: sticky;
      bottom: 0;
      z-index: 2;
      &.covered {
        visibility: hidden;
      }
      .footer-card {
        @media (max-width: 480px) {
          border-bottom-right-radius: 0;      
          border-bottom-left-radius: 0;
        }
        background-color: var(--color-default-background);
        .footer-header {
          -webkit-tap-highlight-color: rgba(0,0,0,0); /* disable tap highlight on iOS */
          /* background-color: var(--color-default-emphasis-background); */
        }
      }
    }
    &.desktop {
      /* background: linear-gradient(120deg, var(--color-default-background-50) 0%, transparent 100%); */
      grid-column: 26 / -1;
      @media (max-width: 1024px) {
        display: none;
      }
      .footer-card {
        max-width: calc(12 * 56px);
        .footer-header {
          justify-content: space-between;
        }
      }
    }
  }

  /* The calendar column; the booking sheet sits at its bottom and is clipped while it slides out.
     Clipped vertically only, so the sheet's shadow is not cut off at the sides. */
  .booking-column {
    position: relative;
    overflow: visible clip;
    /* the sheet is as wide as the calendar, which is centred on tablets */
    --booking-sheet-max-width: calc(8 * 56px);
    height: calc(100svh - var(--dynamic-island-bottom-edge) - var(--scrollbar-width));
    grid-column: 1 / -1;
    @media (min-width: 1025px) {
      max-width: calc(8 * 56px);
      justify-self: flex-end;
      width: 100%;
      grid-column: var(--dynamic-island-track-start) / 26;
    }
  }

  .overflow-wrapper {
    overflow-x: auto;
    overflow-y: scroll;
    scrollbar-width: thin;
    scroll-behavior: smooth;
    /* To calculate the height of the calendar content in cqw units, we need to set the container type to inline-size. */
    container-type: inline-size;

    /* when reaching the boundary of a scrolling area, the browser should not scroll the content.
      including the vertical pull-to-refresh gesture */
    overscroll-behavior-block: contain;
    overscroll-behavior-inline: contain;
    touch-action: pan-y; /* Allow vertical scrolling, but handle horizontal ourselves */

    height: 100%;
    display: flex;
    scroll-snap-type: x mandatory;
  }

  .calendar-content {
    /* we need an explicit height for sticky to work */
    /** Both iOS and MacOS Safari need explicit height, max-content is not sufficient. */
    /* Since the grid is dynamic in width, we need to use cqw units to calculate the height. */
    /* min-height: max-content; */
    /* min-height: calc(48 * calc(100cqw / 8));  */
    min-height: fit-content;
    max-width: calc(8 * 56px);
    margin-inline: auto;
  }

  .calendar-days-legend {
    position: sticky;
    top: 0;
    width: max-content;
    min-width: 100%;
    background-color: var(--color-default-background);
    z-index: 2;
    /* We need to clip the shadow of the legend, so it doesn't bleed over the edge of the calendar */
    clip-path: inset(0px 0px -9999px 0px);
    /* There's a 1px white "Blitzer" above the legend in Safari, need to account for that */
    &:before {
      content: '';
      display: block;
      height: 1px;
      background-color: var(--color-default-background);
      top: -1px;
      position: absolute;
      width: 100%;
    }
    .row-1 {
      grid-column: 1 / -1;
    }
    > .legend-item {
      display: grid;
      grid-template-rows: repeat(3, 1.8rem);
      min-width: max(var(--touchable-target-min-size), 100%);
      min-height: var(--touchable-target-min-size);
      max-width: calc((100vw - 2 * var(--page-inline-spacing-min)) / 8);
      .day-number {
        position: relative;
      }
      .day-number.today {
        * {
          color: var(--color-default-background);
        }
        &:before {
          content: '';
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          z-index: -1;
          width: max(100%, 1.5em);
          min-height: 1.5em;
          background: var(--color-default-foreground);
          border-radius: var(--round);
          margin: auto;
        }
      }
    }
  }


  .calendar-days-slots {      
    position: relative;
    margin-inline-start: -1px; /* fix white edging on the right of border (visible to the right side of the legend) */
    > * {
      scroll-snap-align: start;
    }

    @media (max-width: 400px) {
      > .time-slot-legend {
        grid-column: 1 / 2;
        pointer-events: none;
        z-index: 1;
      }
      > .date-column.weekday-0 {
        grid-column: 1 / 2;
      }
      > .date-column, > .time-slot-legend {
        grid-row: 2 / 3;
      }
    }
  }

  .grid-8-columns {
    min-width: 0;
    display: grid;
    width: 100%;
    grid-template-columns: repeat(8, minmax(var(--touchable-target-min-size), 1fr));
    @media (max-width: 400px) {
      grid-template-columns: repeat(7, minmax(var(--touchable-target-min-size), 1fr));
      &.calendar-days-legend {
        .legend-item:nth-child(2) {
          display: none;
        }
      }
    }
  }

  .now-indicator {
    position: absolute;
    --slot-width: calc(100% / 8);
    /* left: var(--slot-width);
    width: calc(var(--slot-width) * 7); */
    width: 100%;
    height: 3px;
    transform: translateY(-1px);
    background: var(--color-default-foreground);
    border: 1px solid var(--color-default-emphasis-background);
    z-index: 1;
    pointer-events: none;
    min-height: 0;
    display: flex;
    align-items: center;
    span {
      width: var(--slot-width);
      text-align: center;
      background: var(--color-default-emphasis-background);
    }
  }


  .time-slot, .slot {
    width: 100%;
    min-height: var(--touchable-target-min-size);
    aspect-ratio: 1;
    display: flex;
    place-content: center;
  }
  .slot {
    position: relative;
    align-items: center;
    -webkit-tap-highlight-color: rgba(0,0,0,0); /* disable tap highlight on iOS */
    box-shadow: inset 0 0 0 0.5px var(--color-default-background-100), 0 0 0 0.5px var(--color-default-background-100);
    &:focus-visible {
      box-shadow: var(--selected-border);
    }
  }
  /* Slots are selected by dragging across them, not by selecting their (empty) text */
  .calendar-days-slots {
    -webkit-user-select: none;
    user-select: none;
  }

  .booked-by-none.future {
    @media (hover: hover) {
      &:not(:disabled):not(.pend):hover {
        background: radial-gradient(circle, var(--color-default-emphasis-background) 30%, transparent calc(30% + 1px));
      }
    }
  }

  /* A day with selected slots, also while its column is scrolled out of view */
  .selection-dot {
    position: absolute;
    left: 50%;
    bottom: -3px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    transform: translateX(-50%);
    background: var(--color-default-emphasis-background);
    box-shadow: inset 0 0 0 1px oklch(from var(--color-default-emphasis-background) calc(l * 0.8) c h);
  }
  
  /* Slot states */
  .past, .booked-by-none:disabled:not(.outside-business-hours), .booked-by-other-view-for-admin {
    --stripe: var(--color-default-background-25);
    --angle: -45deg;
  }
  .outside-business-hours {
    --stripe: var(--color-default-background-50);
    --angle: 45deg;
    pointer-events: none;
  }
  .past, .outside-business-hours, .booked-by-none:disabled, .booked-by-other-view-for-admin {
    --stroke-width: 2.42px; 
    background: repeating-linear-gradient(
      var(--angle),
      var(--stripe) 0,
      var(--stripe) var(--stroke-width),
      transparent var(--stroke-width),
      transparent calc(2 * var(--stroke-width))
      );
  }
  .booked-by-other:not(.booked-by-other-view-for-admin) {
    --stroke-color: var(--color-default-background-200);
    --stroke-width: 2px;
    box-shadow: 
      inset 0 0 0 0.5px var(--color-default-background-100),
      0 0 0 0.5px var(--color-default-background-100),
      inset 0 0 0 10px var(--color-default-background);
    background: 
      linear-gradient(to top right, transparent calc(50% - var(--stroke-width)), var(--stroke-color) calc(50% - var(--stroke-width)), var(--stroke-color) calc(50% + var(--stroke-width)), transparent calc(50% + var(--stroke-width))),
      linear-gradient(to top left, transparent calc(50% - var(--stroke-width)), var(--stroke-color) calc(50% - var(--stroke-width)), var(--stroke-color) calc(50% + var(--stroke-width)), transparent calc(50% + var(--stroke-width)));
  }
  .booked-by-other-view-for-admin {
    --avatar-fill: var(--color-default-background-100);
    :global(img) {
      border-radius: var(--round);
    }
  } 
  /* Beads. Own bookings are solid; a selection is the same bead, lighter, with its ring dissolved into
     dots at the same radius: lime to book, red to cancel. Equal neighbours join into a column. */
  .booked-by-me {
    --stroke-color: var(--color-default-emphasis-background);
    &.past {
      --stroke-color: oklch(from var(--color-default-emphasis-background) l 0 h);
    }
    &:not(.pend) {
      --fill: var(--stroke-color);
      --ring: oklch(from var(--stroke-color) calc(l * 0.9) c h);
      --bead: radial-gradient(circle, var(--color-default-background) calc(30% - 1px), var(--ring) 30%, var(--ring) calc(30% + 4px), var(--fill) calc(30% + 5px), var(--fill) 67%, transparent calc(67% + 1px));
    }
  }
  .pend {
    --bead: radial-gradient(circle, var(--color-default-background) calc(30% - 1px), var(--fill) 30%, var(--fill) 67%, transparent calc(67% + 1px));
    &::after {
      content: '';
      position: absolute;
      left: 50%;
      top: 50%;
      width: calc(42.4% + 8px);
      aspect-ratio: 1;
      border-radius: 50%;
      border: 4px dotted var(--dots);
      transform: translate(-50%, -50%);
      pointer-events: none;
      animation: pend-pop 0.24s cubic-bezier(0.3, 1.4, 0.5, 1);
    }
  }
  .pend-add {
    --fill: color-mix(in oklab, var(--color-default-emphasis-background) 50%, var(--color-default-background));
    --dots: oklch(from var(--color-default-emphasis-background) calc(l * 0.9) c h);
  }
  .pend-remove {
    --fill: color-mix(in oklab, var(--color-destructive) 16%, var(--color-default-background));
    --dots: color-mix(in oklab, var(--color-destructive) 58%, var(--color-default-background));
  }
  .booked-by-me, .pend {
    background: var(--bead);
    &.chain-up {
      background: var(--bead), linear-gradient(to bottom, var(--fill) 50%, transparent calc(50% + 1px));
    }
    &.chain-down {
      background: var(--bead), linear-gradient(to top, var(--fill) 50%, transparent calc(50% + 1px));
    }
    &.chain-up.chain-down {
      background: var(--bead), var(--fill);
    }
  }
  @keyframes pend-pop {
    from { transform: translate(-50%, -50%) scale(0.5); }
  }

  /* Pressed, touch only (a mouse changes the slot on press): a free slot grows the lime dot, which is
     also the press-and-hold timer; a selected slot or booking dims. The short delay keeps a scroll
     that starts on a slot from flashing it. */
  .pressed.booked-by-none:not(.pend)::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    width: 42.4%;
    aspect-ratio: 1;
    border-radius: 50%;
    background: var(--color-default-emphasis-background);
    transform: translate(-50%, -50%);
    animation: press-grow 0.24s 0.08s ease-out both;
  }
  @keyframes press-grow {
    from { transform: translate(-50%, -50%) scale(0); }
  }
  .pressed.pend, .pressed.booked-by-me {
    opacity: 0.55;
  }
  @media (prefers-reduced-motion: reduce) {
    .pend::after, .pressed::after {
      animation: none;
    }
  }

  .nighttime-slot {
    aspect-ratio: var(--night-time-slot-scale) / 1;
    min-height: calc(0.3 * var(--touchable-target-min-size));
    &.time-slot:nth-of-type(2n) {
      visibility: hidden;
    }
  }


  .instructions {
    .slot {
      box-shadow: none;
      width: calc(var(--touchable-target-min-size)* 0.7);
      pointer-events: none;
      min-height: 0;
      &:not(.booked-by-other) {
        background-color: var(--color-default-background);
        border-radius: 100%;
      }
      &.booked-by-other {
        scale: 0.7;
      }
    }
  }

  .instructions-desktop-tooltip {
    display: none;
    margin-block-start: auto;
    @media (min-width: 1025px) {
      display: flex;
    }
  }

  .resource-overview {
    text-align: left;
    @media (min-width: 450px) {
      text-align: right;
    }
  }

</style>